import type { BodyUUID, SiteUUID } from "../types/scene";
import type { SceneStoreState } from "../stores/sceneStore.types";

/** Minimal scene slice for EE resolution (bodies + sites only). */
export type SceneEeResolveSlice = Pick<SceneStoreState, "bodies" | "sites">;

function resolveBodyUuidOwningSite(scene: SceneEeResolveSlice, siteUuid: string): BodyUUID | null {
  const bodies = scene?.bodies || {};
  const matches = Object.values(bodies).filter((body) =>
    Array.isArray(body?.sites) ? body.sites.includes(siteUuid as SiteUUID) : false
  );
  if (!matches.length) return null;
  if (matches.length === 1) {
    const only = matches[0]?.uuid ? String(matches[0].uuid) : null;
    return (only as BodyUUID) || null;
  }
  const bodyByUuid = new Map<string, (typeof matches)[0]>();
  const childUuidSet = new Set<string>();
  Object.values(bodies).forEach((b) => {
    const u = b?.uuid ? String(b.uuid) : null;
    if (!u) return;
    bodyByUuid.set(u, b);
    const children = Array.isArray(b?.children) ? b.children : [];
    children.forEach((c) => {
      if (c != null) childUuidSet.add(String(c));
    });
  });
  const depthByUuid = new Map<string, number>();
  const visit = (u: string, d: number) => {
    if (!u || depthByUuid.has(u)) return;
    depthByUuid.set(u, d);
    const body = bodyByUuid.get(u);
    const children = Array.isArray(body?.children) ? body.children : [];
    children.forEach((c) => visit(String(c), d + 1));
  };
  const roots = Array.from(bodyByUuid.keys()).filter((u) => !childUuidSet.has(u));
  roots.forEach((u) => visit(u, 0));

  let bestUuid: string | null = null;
  let bestDepth = -1;
  for (const b of matches) {
    const u = b?.uuid ? String(b.uuid) : "";
    if (!u) continue;
    const d = depthByUuid.get(u) ?? -1;
    if (d >= bestDepth) {
      bestDepth = d;
      bestUuid = u;
    }
  }
  return (bestUuid as BodyUUID) || null;
}

export function resolveEndEffectorBodyUuid(
  scene: SceneEeResolveSlice,
  ref: string
): { bodyUuid: BodyUUID; useGripperTargetSite: boolean } | null {
  if (!ref) return null;
  if (ref === "gripper_target") {
    const sites = scene?.sites || {};
    const siteEntry = Object.values(sites).find((s) => s?.name === "gripper_target");
    const siteUuid = siteEntry?.uuid ? String(siteEntry.uuid) : null;
    if (!siteUuid) return null;
    const body = resolveBodyUuidOwningSite(scene, siteUuid);
    if (!body) return null;
    return { bodyUuid: body, useGripperTargetSite: true };
  }
  if (scene.bodies?.[ref as BodyUUID]) {
    return { bodyUuid: ref as BodyUUID, useGripperTargetSite: false };
  }
  return null;
}
